import { useState, useEffect, useCallback, useRef } from "react";
import audioRecorder from "../services/audioRecorderService";

/**
 * React hook wrapping the AudioRecorderService
 * Now supports priority flags, haptic feedback, and auto-close
 */
export function useAudioRecorder(sessionId) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [formattedTime, setFormattedTime] = useState("00:00");
  const [transcript, setTranscript] = useState({ final: "", interim: "" });
  const [fullTranscript, setFullTranscript] = useState("");
  const [flaggedNotes, setFlaggedNotes] = useState([]);
  const [currentSection, setCurrentSection] = useState(null);
  const [lastTrigger, setLastTrigger] = useState(null);
  const [error, setError] = useState(null);
  const [isFlagging, setIsFlagging] = useState(false);
  const [flagPriority, setFlagPriority] = useState("normal");
  const [flagAlert, setFlagAlert] = useState(null); // For the big visible alert

  const timerRef = useRef(null);
  const alertTimeoutRef = useRef(null);

  /**
   * Trigger haptic feedback on mobile devices
   */
  const triggerHaptic = useCallback((style = "medium") => {
    try {
      if (navigator.vibrate) {
        if (style === "heavy") {
          navigator.vibrate([100, 50, 100, 50, 200]); // strong pattern for priority
        } else if (style === "medium") {
          navigator.vibrate([80, 40, 80]); // double buzz for normal flag
        } else {
          navigator.vibrate(50); // light tap
        }
      }
    } catch (e) {
      // Haptic not available
    }
  }, []);

  /**
   * Show a big visible alert that auto-dismisses
   */
  const showFlagAlert = useCallback((type, phrase, priority) => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    setFlagAlert({ type, phrase, priority, timestamp: Date.now() });

    alertTimeoutRef.current = setTimeout(() => {
      setFlagAlert(null);
    }, 3500); // Show for 3.5 seconds
  }, []);

  // Set up callbacks
  useEffect(() => {
    audioRecorder.onTranscript = (data) => {
      setTranscript(data);
      if (data.final) {
        setFullTranscript((prev) => prev + " " + data.final);
      }
    };

    audioRecorder.onTriggerDetected = (trigger) => {
      setLastTrigger(trigger);

      if (trigger.type === "realtimeAlert" && trigger.tier === 3) {
        // Tier 3: Real-time supervisor alert — pass ALL fields through
        setIsFlagging(true);
        setFlagPriority("priority");
        triggerHaptic("heavy");
        setFlagAlert({
          type: trigger.type,
          tier: trigger.tier,
          phrase: trigger.phrase,
          priority: "priority",
          category: trigger.category,
          categoryKey: trigger.categoryKey,
          severity: trigger.severity,
          transcriptContext: trigger.transcriptContext,
          timestamp: Date.now(),
        });
        if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
        alertTimeoutRef.current = setTimeout(() => setFlagAlert(null), 5000);
      } else if (trigger.type === "clinicalIndicator" && trigger.tier === 2) {
        // Tier 2: Clinical indicator — local display only
        triggerHaptic("light");
        showFlagAlert("clinicalIndicator", trigger.phrase, "normal");
      } else if (trigger.type === "localFlag" && trigger.tier === 1) {
        // Tier 1: Agent observation — local display only
        triggerHaptic("light");
        showFlagAlert("localFlag", trigger.phrase, "normal");
      } else if (trigger.type === "startNote") {
        setIsFlagging(true);
        setFlagPriority("normal");
        triggerHaptic("medium");
        showFlagAlert("startNote", trigger.phrase, "normal");
      } else if (trigger.type === "priorityFlag") {
        setIsFlagging(true);
        setFlagPriority("priority");
        triggerHaptic("heavy");
        showFlagAlert("priorityFlag", trigger.phrase, "priority");
      } else if (trigger.type === "endNote" || trigger.type === "autoClose") {
        setIsFlagging(false);
        setFlagPriority("normal");
        triggerHaptic("light");
        showFlagAlert("endNote", trigger.phrase, "normal");
      } else if (trigger.type === "medication" || trigger.type === "safety" ||
                 trigger.type === "housing" || trigger.type === "coping") {
        triggerHaptic("light");
        showFlagAlert("section", trigger.phrase, "normal");
      }
    };

    audioRecorder.onFlagStart = (data) => {
      // Additional callback when flag starts
    };

    audioRecorder.onFlaggedNote = (segment) => {
      setFlaggedNotes((prev) => [...prev, segment]);
      setIsFlagging(false);
    };

    audioRecorder.onSectionChange = (section) => {
      setCurrentSection(section);
    };

    audioRecorder.onError = (err) => {
      setError(err.message || "Recording error");
    };

    return () => {
      audioRecorder.onTranscript = null;
      audioRecorder.onTriggerDetected = null;
      audioRecorder.onFlagStart = null;
      audioRecorder.onFlaggedNote = null;
      audioRecorder.onSectionChange = null;
      audioRecorder.onError = null;
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    };
  }, [triggerHaptic, showFlagAlert]);

  // Timer
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setElapsedTime(audioRecorder.getElapsedTime());
        setFormattedTime(audioRecorder.getFormattedTime());
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording, isPaused]);

  const initialize = useCallback(
    async (customTriggers = null) => {
      const result = await audioRecorder.initialize(sessionId, customTriggers);
      if (!result.success) setError(result.error);
      return result;
    },
    [sessionId]
  );

  const startRecording = useCallback(() => {
    audioRecorder.start();
    setIsRecording(true);
    setIsPaused(false);
    setError(null);
  }, []);

  const pauseRecording = useCallback(() => {
    audioRecorder.pause();
    setIsPaused(true);
  }, []);

  const resumeRecording = useCallback(() => {
    audioRecorder.resume();
    setIsPaused(false);
  }, []);

  const stopRecording = useCallback(async () => {
    audioRecorder.stop();
    setIsRecording(false);
    setIsPaused(false);
    return audioRecorder.getSessionData();
  }, []);

  const getSessionData = useCallback(() => {
    return audioRecorder.getSessionData();
  }, []);

  /**
   * Manually trigger a flag (from the Flag button)
   */
  const manualFlag = useCallback((priority = "normal") => {
    if (isFlagging) {
      // Close current flag
      audioRecorder.closeCurrentFlag();
      setIsFlagging(false);
      triggerHaptic("light");
      showFlagAlert("endNote", "Manual close", "normal");
    } else {
      // Start new flag
      audioRecorder.startFlag(priority, "Manual flag");
      audioRecorder.startAutoCloseTimer();
      setIsFlagging(true);
      setFlagPriority(priority);
      triggerHaptic(priority === "priority" ? "heavy" : "medium");
      showFlagAlert("startNote", "Manual flag", priority);
    }
  }, [isFlagging, triggerHaptic, showFlagAlert]);

  const cleanup = useCallback(() => {
    audioRecorder.destroy();
    setIsRecording(false);
    setIsPaused(false);
    setElapsedTime(0);
    setFormattedTime("00:00");
    setTranscript({ final: "", interim: "" });
    setFullTranscript("");
    setFlaggedNotes([]);
    setCurrentSection(null);
    setLastTrigger(null);
    setError(null);
    setIsFlagging(false);
    setFlagPriority("normal");
    setFlagAlert(null);
  }, []);

  return {
    isRecording,
    isPaused,
    isFlagging,
    flagPriority,
    flagAlert,
    elapsedTime,
    formattedTime,
    transcript,
    fullTranscript,
    flaggedNotes,
    currentSection,
    lastTrigger,
    error,

    initialize,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getSessionData,
    manualFlag,
    cleanup,
  };
}